// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

interface MrqQueryParams {
  name?: string;
  magnetType?: string;
  id?: string;
}
// Restricted (c) Siemens Healthineers AG / Siemens Medical Solutions USA, 2024, All rights reserved.

import { CommonModule, Location } from '@angular/common';
import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { of, switchMap } from 'rxjs';
import { WeekendHighlightDirective } from 'src/app/directives/weekend-highlight.directive';
import { LocalizedDatePipe } from 'src/app/modules/shared/pipes/localizedDate.pipe';
import { Mrq10KSystemModel } from '../../../../api/remote-api/.portal.generated/models/mrq-10-k-system-model';
import { Mrq4KSystemModel } from '../../../../api/remote-api/.portal.generated/models/mrq-4-k-system-model';
import { MrqDryCoolSystemModel } from '../../../../api/remote-api/.portal.generated/models/mrq-dry-cool-system-model';
import { RouteService } from '../../../portal/services/routeService.service';
import { MrqReportData } from '../../interfaces/mrq.interfaces';
import { MrqReportService } from '../../services/mrq-report/mrq-report.service';

type MrqBackendData = {
  magnet4k: Mrq4KSystemModel[];
  magnet10k: Mrq10KSystemModel[];
  magnetDryCool: MrqDryCoolSystemModel[];
};

@Component({
  selector: 'app-mrq-details',
  templateUrl: './mrq-details.component.html',
  styleUrls: ['./mrq-details.component.scss'],
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    TranslateModule,
    MatTableModule,
    LocalizedDatePipe,
    WeekendHighlightDirective,
  ],
})
export class MrqDetailsComponent {
  private route = inject(ActivatedRoute);
  protected location = inject(Location);
  private mrqReportService = inject(MrqReportService);
  private routeService = inject(RouteService);

  // Signals for query parameters
  private queryParams = toSignal<MrqQueryParams | undefined>(
    this.route.queryParams,
    { initialValue: undefined },
  );
  name = computed(() => this.queryParams()?.name ?? 'N/A');
  magnetType = computed(() => this.queryParams()?.magnetType ?? '4K');

  // Table configuration
  displayedColumns: string[] = ['date', 'value'];

  // Signals for the selected item
  private selectedColumnId = computed(() => this.queryParams()?.id ?? '');
  private selectedMagnetType = computed(
    () => this.queryParams()?.magnetType ?? '4K',
  );

  // Store all MRQ data fetched from backend as a signal
  private allMrqData = this.createAllMrqDataSignal();

  /**
   * Creates a signal that fetches all MRQ data from the backend for the current system.
   */
  private createAllMrqDataSignal() {
    return toSignal<MrqBackendData | undefined>(
      this.routeService.currentSystem$.pipe(
        switchMap((system) => this.getMrqDataForSystem(system)),
      ),
      { initialValue: undefined },
    );
  }

  // Returns observable of MRQ data for a valid system, otherwise undefined
  private getMrqDataForSystem(
    system:
      | { systemSerialNumber: string; systemMaterialNumber: number }
      | false
      | null
      | undefined,
  ) {
    return system && typeof system === 'object'
      ? this.mrqReportService.getMrqReportData(
          system.systemSerialNumber,
          system.systemMaterialNumber,
        )
      : of(undefined);
  }

  // Computed historical data based on backend data
  historicalData = computed(() => {
    const data = this.allMrqData();
    if (!data) return [];
    return this.getHistoricalDataForColumn(
      this.selectedColumnId(),
      this.selectedMagnetType(),
      data,
    );
  });

  /**
   * Returns historical data for a given column and magnet type from the backend data
   */
  private getHistoricalDataForColumn(
    columnId: string,
    magnetType: string,
    allData: MrqBackendData,
  ) {
    const dataArr = this.selectDataByMagnetType(magnetType, allData);
    return this.mapAndSortHistoricalData(dataArr, columnId);
  }

  /**
   * Selects the correct MRQ data array based on magnet type.
   */
  private selectDataByMagnetType(
    magnetType: string,
    allData: MrqBackendData,
  ): MrqReportData[] {
    switch (magnetType) {
      case '4K':
        return this.mrqReportService.mapToMrqReportData(allData.magnet4k, '4K');
      case '10K':
        return this.mrqReportService.mapToMrqReportData(
          allData.magnet10k,
          '10K',
        );
      case 'DryCool':
        return this.mrqReportService.mapToMrqReportData(
          allData.magnetDryCool,
          'DryCool',
        );
      default:
        return [];
    }
  }

  /**
   * Maps and sorts the MRQ data array for the given column.
   */
  private mapAndSortHistoricalData(dataArr: MrqReportData[], columnId: string) {
    if (!Array.isArray(dataArr)) return [];
    return dataArr
      .map((report: MrqReportData) => ({
        date: report.date,
        value: report.values?.[columnId] ?? '-',
      }))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }
}
